import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Brain, Gauge, Gift, Compass, Loader2, ExternalLink, Rss,
  Check, Send, Copy, Trash2, MessageSquare, ShieldAlert,
  CalendarClock, CheckCircle2, AlarmClockOff, ScrollText, HeartHandshake, Cpu, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  PageHeader, Spinner, GoldButton, GhostButton, inputCls,
  StageBadge, PlatformTag, STAGE_LABELS, PIPELINE_STAGES,
} from "@/components/app/widgets";

const AI_ACTIONS = [
  { key: "research", label: "Run Research", icon: Brain },
  { key: "score", label: "Calculate Score", icon: Gauge },
  { key: "value-strategy", label: "Value Strategy", icon: Gift },
  { key: "next-action", label: "Next Best Action", icon: Compass },
];

const OUTREACH_TYPES = [
  ["email", "Email"], ["dm", "Direct Message"], ["partnership_proposal", "Partnership Proposal"],
  ["follow_up", "Follow-up"], ["webinar_proposal", "Webinar Proposal"],
  ["interview_proposal", "Interview Proposal"], ["resource_offer", "Resource Offer"],
];

const PROV_STYLE = {
  verified: "text-emerald-400",
  inference: "text-amber-400",
  unavailable: "text-zinc-600",
  unknown: "text-zinc-600",
};

function Prov({ v }) {
  return <span className={`text-[10px] font-semibold uppercase tracking-wider ${PROV_STYLE[v] || PROV_STYLE.unavailable}`}>{v || "unknown"}</span>;
}

function KV({ k, v, prov }) {
  if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[200px_1fr] sm:gap-4">
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
        {k} {prov && <Prov v={prov} />}
      </span>
      {Array.isArray(v) ? (
        <ul className="list-inside list-disc space-y-1 text-sm text-zinc-300">
          {v.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      ) : (
        <span className="text-sm leading-relaxed text-zinc-300">{v}</span>
      )}
    </div>
  );
}

const DEEP_QUESTIONS = [
  ["who_is_this_partner", "Who is this partner?"],
  ["who_is_their_audience", "Who is their audience?"],
  ["what_audience_cares_about", "What does their audience care about?"],
  ["problems_they_discuss", "What problems are they discussing?"],
  ["value_we_can_provide", "What value can we provide?"],
  ["best_partnership_angle", "Best partnership angle?"],
  ["why_kpb_should_approach", "Why should we approach them?"],
  ["do_not_pitch_yet", "What should we NOT pitch yet?"],
];

export default function ProspectDetail() {
  const { prospectId } = useParams();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const [outreachType, setOutreachType] = useState("email");
  const [hypothesis, setHypothesis] = useState("");
  const [expected, setExpected] = useState("");
  const [note, setNote] = useState("");
  const [respForms, setRespForms] = useState({});
  const [fuDate, setFuDate] = useState("");
  const [fuNote, setFuNote] = useState("");

  const load = () =>
    api.get(`/prospects/${prospectId}`).then((r) => setP(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [prospectId]);

  const runAction = async (key) => {
    setBusy(key);
    try {
      await api.post(`/prospects/${p.prospect_id}/${key}`, {});
      toast.success("AI analysis complete");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const generateOutreach = async () => {
    setBusy("outreach");
    try {
      await api.post(`/prospects/${p.prospect_id}/outreach`, {
        message_type: outreachType, hypothesis, expected_outcome: expected,
      });
      toast.success("Draft generated — review and approve before sending");
      setHypothesis(""); setExpected("");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const recordResponse = async (messageId) => {
    const form = respForms[messageId] || {};
    if (!form.classification) {
      toast.error("Select a response classification first");
      return;
    }
    setBusy(`resp-${messageId}`);
    try {
      await api.post(`/outreach/${messageId}/response`, {
        classification: form.classification, note: form.note || "",
      });
      toast.success("Response recorded");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const setStage = async (stage) => {
    try {
      await api.post(`/prospects/${p.prospect_id}/stage`, { stage });
      toast.success(`Moved to ${STAGE_LABELS[stage]}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/prospects/${p.prospect_id}/interactions`, { type: "note", note: note.trim() });
      setNote("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const setMsgStatus = async (id, status) => {
    try {
      await api.post(`/outreach/${id}/status`, { status });
      toast.success(status === "approved" ? "Draft approved" : "Marked as sent");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const followUp = async (action, body) => {
    setBusy(`fu-${action}`);
    try {
      await api.post(`/prospects/${p.prospect_id}/follow-up${action === "schedule" ? "" : `/${action}`}`, body || {});
      toast.success("Follow-up updated");
      setFuDate(""); setFuNote("");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this prospect?")) return;
    try {
      await api.delete(`/prospects/${p.prospect_id}`);
      toast.success("Prospect deleted");
      window.history.back();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!p) return <Spinner label="Loading prospect" />;
  const intel = p.intelligence;
  const score = p.score;
  const vs = p.value_strategy;
  const na = p.next_action;
  const enr = p.enrichment;
  const deep = p.deep_research;
  const bp = p.blueprint;
  const fu = p.follow_up;
  const canDeepResearch = ["podcast", "youtube"].includes(p.platform) || !!p.url;

  return (
    <div data-testid="prospect-detail-page">
      <PageHeader
        title={p.name}
        sub={
          <span className="flex flex-wrap items-center gap-3">
            <PlatformTag platform={p.platform} />
            {p.creator && <span>{p.creator}</span>}
            {p.url && (
              <a href={p.url} target="_blank" rel="noreferrer" data-testid="prospect-url-link" className="inline-flex items-center gap-1 text-[#D4AF37] hover:brightness-110">
                View source <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </span>
        }
      >
        <GhostButton onClick={remove} data-testid="delete-prospect-button"><Trash2 className="h-4 w-4" /> Delete</GhostButton>
      </PageHeader>

      <div className="mb-8 flex flex-wrap items-center gap-3 border border-[#27272A] bg-[#0A0A0A] p-5">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">AI Actions</span>
        {canDeepResearch && (
          <GoldButton onClick={() => runAction("deep-research")} disabled={!!busy} data-testid="run-deep-research-button" className="px-4 py-2">
            {busy === "deep-research" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rss className="h-3.5 w-3.5" />}
            Deep Research
          </GoldButton>
        )}
        {AI_ACTIONS.map((a) => (
          <GoldButton key={a.key} onClick={() => runAction(a.key)} disabled={!!busy} data-testid={`run-${a.key}-button`} className="px-4 py-2">
            {busy === a.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <a.icon className="h-3.5 w-3.5" />}
            {a.label}
          </GoldButton>
        ))}
        <GoldButton onClick={() => runAction("match")} disabled={!!busy} data-testid="run-match-button" className="border border-[#D4AF37]/60 bg-transparent px-4 py-2 !text-[#D4AF37] hover:brightness-125">
          {busy === "match" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <HeartHandshake className="h-3.5 w-3.5" />}
          Match Analysis
        </GoldButton>
        <GoldButton onClick={() => runAction("engine-analysis")} disabled={!!busy} data-testid="run-engine-analysis-button" className="border border-[#D4AF37]/60 bg-transparent px-4 py-2 !text-[#D4AF37] hover:brightness-125">
          {busy === "engine-analysis" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Cpu className="h-3.5 w-3.5" />}
          Engine Analysis
        </GoldButton>
        {["tiktok", "instagram", "facebook"].includes(p.platform) && (
          <GoldButton onClick={() => runAction("dm-strategy")} disabled={!!busy} data-testid="run-dm-strategy-button" className="border border-[#D4AF37]/60 bg-transparent px-4 py-2 !text-[#D4AF37] hover:brightness-125">
            {busy === "dm-strategy" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageCircle className="h-3.5 w-3.5" />}
            DM Strategy
          </GoldButton>
        )}
        <GoldButton onClick={() => runAction("blueprint")} disabled={!!busy} data-testid="run-blueprint-button" className="border border-[#D4AF37]/60 bg-transparent px-4 py-2 !text-[#D4AF37] hover:brightness-125">
          {busy === "blueprint" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScrollText className="h-3.5 w-3.5" />}
          Partnership Blueprint
        </GoldButton>
        <span className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-600">
          <ShieldAlert className="h-3.5 w-3.5 text-[#047857]" /> AI-assisted · human-approved
        </span>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          {/* Deep Research */}
          {(deep || enr) && (
            <section className="border border-[#D4AF37]/30 bg-[#0A0A0A] p-6" data-testid="deep-research-section">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                  <Rss className="h-4 w-4" /> Deep Research — {p.platform}
                </h2>
                {deep?.confidence && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">confidence: {deep.confidence}</span>
                )}
              </div>
              {enr && (
                <div className="mt-4 grid gap-2 border border-white/10 p-4 text-xs sm:grid-cols-2">
                  <p><span className="text-zinc-500">Host:</span> <span className="text-zinc-200">{enr.host || "unknown"}</span> <Prov v={enr.host ? "verified" : "unavailable"} /></p>
                  <p><span className="text-zinc-500">Contact:</span> <span className="text-zinc-200">{enr.contact_email || "unknown"}</span> <Prov v={enr.contact_email ? "verified" : "unavailable"} /></p>
                  <p><span className="text-zinc-500">Website:</span> {enr.website ? <a href={enr.website} target="_blank" rel="noreferrer" className="text-[#D4AF37]">{enr.website}</a> : <span className="text-zinc-200">unknown</span>}</p>
                  <p><span className="text-zinc-500">Frequency:</span> <span className="text-zinc-200">{enr.episode_frequency || "unknown"}</span> <Prov v={enr.episode_frequency ? "verified" : "unavailable"} /></p>
                  {enr.subscriber_count && <p><span className="text-zinc-500">Audience:</span> <span className="text-zinc-200">{enr.subscriber_count}</span> <Prov v="verified" /></p>}
                  {enr.rss_feed && <p className="sm:col-span-2 truncate"><span className="text-zinc-500">RSS:</span> <a href={enr.rss_feed} target="_blank" rel="noreferrer" className="text-[#D4AF37]">{enr.rss_feed}</a></p>}
                </div>
              )}
              {deep && (
                <div className="mt-2 divide-y divide-white/5">
                  {DEEP_QUESTIONS.map(([k, label]) => (
                    <KV key={k} k={label} v={deep[k]} prov={deep.field_provenance?.[k]} />
                  ))}
                  <KV k="Recurring themes" v={deep.recurring_themes} prov="verified" />
                  <KV k="Relevant episodes" v={deep.relevant_episodes} prov="verified" />
                  <KV k="Collaboration opportunities" v={deep.collaboration_opportunities} prov="inference" />
                </div>
              )}
              {enr?.recent_episodes?.length > 0 && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">Recent content <Prov v="verified" /></p>
                  <ul className="mt-2 space-y-2">
                    {enr.recent_episodes.slice(0, 6).map((ep, i) => (
                      <li key={i} className="flex items-baseline gap-3 text-sm">
                        <span className="shrink-0 text-[10px] text-zinc-600">{ep.published ? new Date(ep.published).toLocaleDateString() : ""}</span>
                        <span className="text-zinc-300">{ep.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Engine Analysis */}
          {p.engine_analysis && (
            <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="engine-analysis-section">
              <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                <Cpu className="h-4 w-4 text-[#D4AF37]" /> {p.platform} Engine Analysis
                <span className="ml-auto text-[10px] normal-case tracking-normal text-zinc-600">confidence: {p.engine_analysis.confidence}</span>
              </h2>
              <div className="mt-2 divide-y divide-white/5">
                {Object.entries(p.engine_analysis)
                  .filter(([k]) => !["engine", "generated_at", "confidence"].includes(k))
                  .map(([k, v]) => (
                    <KV key={k} k={k.replace(/_/g, " ")} v={v} />
                  ))}
              </div>
            </section>
          )}

          {/* DM Strategy */}
          {p.dm_strategy && (
            <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="dm-strategy-section">
              <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                <MessageCircle className="h-4 w-4 text-[#D4AF37]" /> DM Strategy
                <span className="ml-auto text-[10px] normal-case tracking-normal text-zinc-600">AI-assisted · human-approved · never mass messaging</span>
              </h2>
              <div className="mt-4 space-y-4">
                <p className="text-sm leading-relaxed text-zinc-300">{p.dm_strategy.strategy_summary}</p>
                {p.dm_strategy.opener_draft && (
                  <div className="border border-white/10 bg-black/30 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Opener draft (awaiting your approval below)</p>
                    <p className="mt-2 text-sm italic leading-relaxed text-zinc-200">{p.dm_strategy.opener_draft}</p>
                  </div>
                )}
                {p.dm_strategy.value_message_draft && (
                  <div className="border border-white/10 bg-black/30 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Value message</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-200">{p.dm_strategy.value_message_draft}</p>
                  </div>
                )}
                <div className="grid gap-3 text-xs md:grid-cols-2">
                  <p><span className="font-semibold text-zinc-300">Follow-up rule:</span> <span className="text-zinc-400">{p.dm_strategy.follow_up_rule}</span></p>
                  <p><span className="font-semibold text-zinc-300">Best timing:</span> <span className="text-zinc-400">{p.dm_strategy.best_timing}</span></p>
                </div>
                {p.dm_strategy.stop_conditions?.length > 0 && (
                  <p className="text-xs text-amber-400">Stop conditions: {p.dm_strategy.stop_conditions.join(" · ")}</p>
                )}
              </div>
            </section>
          )}

          {/* Partner Match */}
          {p.match && (
            <section className="border border-[#D4AF37]/40 bg-[#0A0A0A] p-6" data-testid="match-section">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                  <HeartHandshake className="h-4 w-4" /> Partner Match
                </h2>
                <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">confidence: {p.match.confidence}</span>
              </div>
              <p className="mt-4 font-serif text-4xl font-semibold">
                {p.match.match_score}<span className="text-lg text-zinc-500">/100</span>
              </p>
              <ul className="mt-4 space-y-2">
                {Object.entries(p.match.sub_scores || {}).map(([k, v]) => (
                  <li key={k} className="flex items-center gap-3 text-xs">
                    <span className="w-52 shrink-0 capitalize text-zinc-400">{k.replace(/_/g, " ")}</span>
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${v || 0}%` }} />
                    </div>
                    <span className="w-8 text-right font-semibold text-zinc-300">{v}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {[["Why this partner?", p.match.why_this_partner], ["Why this product?", p.match.why_this_product],
                  ["Why this audience?", p.match.why_this_audience], ["Why this format?", p.match.why_this_partnership_format]].map(([label, val]) => (
                  <div key={label} className="border border-white/10 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-zinc-300">{val}</p>
                  </div>
                ))}
              </div>
              {p.match.recommended_value_first_offer && (
                <p className="mt-4 border-l-2 border-[#D4AF37] pl-4 text-sm italic text-zinc-300">
                  Recommended value-first offer: {p.match.recommended_value_first_offer}
                </p>
              )}
            </section>
          )}

          {/* Partnership Blueprint */}
          {bp && (
            <section className="border border-[#D4AF37]/50 bg-gradient-to-b from-[#D4AF37]/[0.07] to-transparent p-6 sm:p-8" data-testid="blueprint-section">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#D4AF37]">
                  <ScrollText className="h-5 w-5" /> Partnership Blueprint
                </h2>
                {bp.confidence && <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">confidence: {bp.confidence}</span>}
              </div>
              <div className="mt-5 space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="border border-white/10 bg-black/30 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Who?</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-300">{bp.who}</p>
                  </div>
                  <div className="border border-white/10 bg-black/30 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Why strategically valuable?</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-300">{bp.why_strategic}</p>
                  </div>
                </div>
                {bp.audience_pain?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Audience pain</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-zinc-300">
                      {bp.audience_pain.map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>
                )}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="border border-emerald-500/30 bg-emerald-500/5 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400">Value first</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-200">{bp.value_first}</p>
                  </div>
                  <div className="border border-red-500/30 bg-red-500/5 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-400">What not to do</p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-200">{bp.what_not_to_do}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Best partnership</p>
                  <p className="mt-2 font-serif text-lg font-semibold">{bp.best_partnership}</p>
                </div>
                {bp.approach_strategy?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Approach strategy</p>
                    <ol className="mt-3 space-y-3">
                      {bp.approach_strategy.map((s) => (
                        <li key={s.step} className="flex gap-4">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/50 font-serif text-sm italic text-[#D4AF37]">{s.step}</span>
                          <div>
                            <p className="text-sm font-semibold text-white">{s.action}</p>
                            <p className="mt-0.5 text-sm text-zinc-400">{s.detail}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {bp.conversion_path?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Conversion path</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {bp.conversion_path.map((c, i) => (
                        <div key={i} className="border border-white/10 bg-black/30 p-4">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{i + 1} · {c.stage}</p>
                          <p className="mt-1.5 text-xs leading-relaxed text-zinc-300">{c.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {bp.outreach_strategy && (
                  <div className="border border-white/10 bg-black/30 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Outreach strategy</p>
                    <p className="mt-2 text-sm text-zinc-300">
                      Channel: <span className="font-semibold text-white">{bp.outreach_strategy.recommended_channel}</span> — {bp.outreach_strategy.angle}
                    </p>
                    {bp.outreach_strategy.draft_body && (
                      <p className="mt-2 text-xs text-zinc-500">
                        A personalized draft was added to Outreach Drafts below — it awaits your approval before anything is sent.
                      </p>
                    )}
                  </div>
                )}
                {bp.follow_up_strategy && (
                  <div className="border border-white/10 bg-black/30 p-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Follow-up strategy</p>
                    <p className="mt-2 text-sm text-zinc-300">{bp.follow_up_strategy.timing}</p>
                    {bp.follow_up_strategy.actions?.length > 0 && (
                      <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-zinc-400">
                        {bp.follow_up_strategy.actions.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    )}
                    {bp.follow_up_strategy.anti_spam_note && (
                      <p className="mt-2 text-xs italic text-amber-400">{bp.follow_up_strategy.anti_spam_note}</p>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Score */}
          <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="score-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Partnership Opportunity Score</h2>
              {score && (
                <span className="rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37]">
                  {score.priority}
                </span>
              )}
            </div>
            {score ? (
              <div className="mt-5">
                <p className="font-serif text-5xl font-semibold">
                  {score.total}<span className="text-xl text-zinc-500">/100</span>
                  <span className="ml-3 align-middle font-sans text-xs uppercase tracking-wider text-zinc-500">
                    confidence: {score.confidence}
                  </span>
                </p>
                <ul className="mt-6 space-y-2">
                  {Object.entries(score.weights).map(([k, w]) => (
                    <li key={k} className="flex items-center gap-3 text-xs">
                      <span className="w-44 shrink-0 capitalize text-zinc-400">{k.replace(/_/g, " ")}</span>
                      <span className="w-8 shrink-0 text-zinc-600">{w}%</span>
                      <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
                        <div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${score.breakdown?.[k] || 0}%` }} />
                      </div>
                      <span className="w-8 text-right font-semibold text-zinc-300">{score.breakdown?.[k] ?? 0}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 border-l-2 border-[#D4AF37] pl-4 text-sm italic leading-relaxed text-zinc-300">
                  {score.rationale}
                </p>
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">Not scored yet — run the Scoring Agent.</p>
            )}
          </section>

          {/* Intelligence */}
          <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="intelligence-section">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Partnership Intelligence</h2>
            {intel ? (
              <div className="mt-2 divide-y divide-white/5">
                <KV k="Who they are" v={intel.who_they_are} />
                <KV k="Audience" v={intel.audience} />
                <KV k="Topics" v={intel.topics} />
                <KV k="Content themes" v={intel.content_themes} />
                <KV k="Recurring questions" v={intel.recurring_questions} />
                <KV k="Gaps / opportunities" v={intel.gaps_opportunities} />
                <KV k="Value we can provide" v={intel.value_we_can_provide} />
                <KV k="Partnership fit" v={intel.partnership_fit} />
                <KV k="Do NOT pitch" v={intel.do_not_pitch} />
                <KV k="Confidence" v={intel.confidence} />
                {intel.data_gaps?.length > 0 && <KV k="Data gaps (unavailable)" v={intel.data_gaps} />}
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No intelligence profile yet — run the Research Agent.</p>
            )}
          </section>

          {/* Value strategy + next action */}
          <div className="grid gap-6 md:grid-cols-2">
            <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="value-strategy-section">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Value-First Strategy</h2>
              {vs ? (
                <div className="mt-2 divide-y divide-white/5">
                  <KV k="Offer first" v={vs.recommended_value_offer} />
                  <KV k="Why" v={vs.why_this_value} />
                  <KV k="Partnership" v={vs.recommended_partnership} />
                  <KV k="Angle" v={vs.suggested_angle} />
                  <KV k="Lead magnet" v={vs.suggested_lead_magnet} />
                  <KV k="Do NOT pitch" v={vs.do_not_pitch} />
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500">Run the Value Agent to decide what to give before you ask.</p>
              )}
            </section>
            <section className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6" data-testid="next-action-section">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">What should I do next?</h2>
              {na ? (
                <div className="mt-4">
                  <p className="font-serif text-lg font-semibold leading-snug">{na.action}</p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{na.reason}</p>
                  <p className="mt-4 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                    Urgency: {na.urgency} {na.suggested_channel ? `· Channel: ${na.suggested_channel}` : ""}
                  </p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-zinc-500">Ask the Strategy Agent for the next best action.</p>
              )}
            </section>
          </div>

          {/* Outreach */}
          <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="outreach-section">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Outreach Drafts</h2>
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <select value={outreachType} onChange={(e) => setOutreachType(e.target.value)} data-testid="outreach-type-select" className={`${inputCls} w-56`}>
                  {OUTREACH_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <GoldButton onClick={generateOutreach} disabled={!!busy} data-testid="generate-outreach-button">
                  {busy === "outreach" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                  Generate Draft
                </GoldButton>
              </div>
              <input
                type="text" value={hypothesis} onChange={(e) => setHypothesis(e.target.value)}
                placeholder="Outreach hypothesis — e.g. 'Providing the free guide before asking may increase response rate'"
                data-testid="outreach-hypothesis-input" className={inputCls}
              />
              <input
                type="text" value={expected} onChange={(e) => setExpected(e.target.value)}
                placeholder="Expected outcome (optional)"
                data-testid="outreach-expected-input" className={inputCls}
              />
            </div>
            <div className="mt-5 space-y-4">
              {(p.outreach_messages || []).map((m) => (
                <div key={m.message_id} data-testid={`outreach-${m.message_id}`} className="border border-white/10 p-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                      {m.type.replace(/_/g, " ")}
                    </span>
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      m.status === "sent" ? "bg-emerald-500/10 text-emerald-400"
                      : m.status === "approved" ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                      : "bg-white/5 text-zinc-400"
                    }`}>
                      {m.status}
                    </span>
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => { navigator.clipboard.writeText(`${m.subject}\n\n${m.body}`); toast.success("Copied"); }} data-testid={`copy-${m.message_id}`} className="p-1.5 text-zinc-500 hover:text-white" title="Copy">
                        <Copy className="h-4 w-4" />
                      </button>
                      {m.status === "draft" && (
                        <button onClick={() => setMsgStatus(m.message_id, "approved")} data-testid={`approve-${m.message_id}`} className="flex items-center gap-1 rounded-full border border-[#D4AF37]/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] hover:bg-[#D4AF37]/10">
                          <Check className="h-3 w-3" /> Approve
                        </button>
                      )}
                      {m.status === "approved" && (
                        <button onClick={() => setMsgStatus(m.message_id, "sent")} data-testid={`sent-${m.message_id}`} className="flex items-center gap-1 rounded-full border border-emerald-500/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-400 hover:bg-emerald-500/10">
                          <Send className="h-3 w-3" /> Mark Sent
                        </button>
                      )}
                    </div>
                  </div>
                  {m.subject && <p className="mt-3 text-sm font-semibold text-white">Subject: {m.subject}</p>}
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{m.body}</p>
                  {m.hypothesis && (
                    <p className="mt-3 border-l-2 border-[#D4AF37]/50 pl-3 text-xs italic text-zinc-400">
                      Hypothesis: {m.hypothesis}{m.expected_outcome ? ` — expected: ${m.expected_outcome}` : ""}
                    </p>
                  )}
                  {m.status === "sent" && !m.response && (
                    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`response-form-${m.message_id}`}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        Record the response (you confirm — never auto-assumed)
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <select
                          value={(respForms[m.message_id] || {}).classification || ""}
                          onChange={(e) => setRespForms({ ...respForms, [m.message_id]: { ...(respForms[m.message_id] || {}), classification: e.target.value } })}
                          data-testid={`response-class-${m.message_id}`}
                          className={`${inputCls} w-64`}
                        >
                          <option value="">Classify response…</option>
                          <option value="no_response">No response</option>
                          <option value="positive">Positive response</option>
                          <option value="negative">Negative response</option>
                          <option value="interested">Interested</option>
                          <option value="wants_info">Wants more information</option>
                          <option value="wants_call">Wants a call</option>
                          <option value="wants_collaboration_proposal">Wants a collaboration proposal</option>
                          <option value="not_interested">Not interested</option>
                        </select>
                        <input
                          type="text"
                          value={(respForms[m.message_id] || {}).note || ""}
                          onChange={(e) => setRespForms({ ...respForms, [m.message_id]: { ...(respForms[m.message_id] || {}), note: e.target.value } })}
                          placeholder="Note (optional)"
                          data-testid={`response-note-${m.message_id}`}
                          className={`${inputCls} min-w-0 flex-1`}
                        />
                        <GoldButton
                          onClick={() => recordResponse(m.message_id)}
                          disabled={busy === `resp-${m.message_id}`}
                          data-testid={`record-response-${m.message_id}`}
                          className="px-4 py-2"
                        >
                          {busy === `resp-${m.message_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Record"}
                        </GoldButton>
                      </div>
                    </div>
                  )}
                  {m.response && (
                    <p className="mt-3 text-xs text-zinc-400">
                      <span className="font-bold uppercase tracking-wider text-zinc-500">Recorded outcome: </span>
                      <span className={m.response.classification === "no_response" || m.response.classification === "negative" || m.response.classification === "not_interested" ? "text-red-400" : "text-emerald-400"}>
                        {m.response.classification.replace(/_/g, " ")}
                      </span>
                      {m.response.note ? ` — ${m.response.note}` : ""}
                    </p>
                  )}
                </div>
              ))}
              {(p.outreach_messages || []).length === 0 && (
                <p className="text-sm text-zinc-500">No drafts yet. Every message is a draft until you approve and send it yourself.</p>
              )}
            </div>
          </section>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <section className="border border-[#27272A] bg-[#0A0A0A] p-6">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Pipeline Stage</h2>
            <div className="mt-4 flex items-center gap-3">
              <StageBadge stage={p.status} />
              {p.analysis?.status && (
                <span data-testid="analysis-status-badge" className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  p.analysis.status === "ANALYZED" ? "bg-emerald-500/10 text-emerald-400"
                  : p.analysis.status === "PROCESSING" ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                  : p.analysis.status === "FAILED" ? "bg-red-500/10 text-red-400"
                  : "bg-white/5 text-zinc-500"
                }`}>
                  {p.analysis.status.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <select value={p.status} onChange={(e) => setStage(e.target.value)} data-testid="stage-select" className={`${inputCls} mt-4`}>
              {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
            {p.campaign && (
              <p className="mt-4 text-xs text-zinc-500">
                Campaign:{" "}
                <Link to={`/app/campaigns/${p.campaign.campaign_id}`} className="text-[#D4AF37] hover:brightness-110">
                  {p.campaign.name}
                </Link>
              </p>
            )}
          </section>

          {/* Follow-up manager */}
          <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="followup-section">
            <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
              <CalendarClock className="h-4 w-4 text-[#D4AF37]" /> Follow-Up
            </h2>
            {fu?.due_date && (
              <p className="mt-3 text-sm text-zinc-300">
                Due <span className="font-semibold text-white">{fu.due_date}</span>{" "}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  fu.status === "completed" ? "bg-emerald-500/10 text-emerald-400" : "bg-[#D4AF37]/10 text-[#D4AF37]"
                }`}>{fu.status}</span>
              </p>
            )}
            {fu?.note && <p className="mt-1 text-xs text-zinc-500">{fu.note}</p>}

            {fu?.recommendation && (
              <div className="mt-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#D4AF37]">
                  AI recommends: {fu.recommendation.action.replace(/_/g, " ")}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-300">{fu.recommendation.what_to_do || fu.recommendation.reason}</p>
                {fu.recommendation.anti_spam_note && (
                  <p className="mt-2 text-[11px] italic text-amber-400">{fu.recommendation.anti_spam_note}</p>
                )}
              </div>
            )}

            <button
              onClick={() => followUp("recommend")} disabled={!!busy}
              data-testid="followup-recommend-button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-50"
            >
              {busy === "fu-recommend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Compass className="h-3.5 w-3.5" />}
              What should I do next?
            </button>

            <div className="mt-4 space-y-2">
              <input
                type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)}
                data-testid="followup-date-input" className={inputCls}
              />
              <input
                type="text" value={fuNote} onChange={(e) => setFuNote(e.target.value)}
                placeholder="Follow-up note (optional)" data-testid="followup-note-input" className={inputCls}
              />
              <div className="flex gap-2">
                <GoldButton onClick={() => fuDate && followUp("schedule", { due_date: fuDate, note: fuNote })} disabled={!fuDate || !!busy} data-testid="followup-schedule-button" className="flex-1 px-3 py-2">
                  Schedule
                </GoldButton>
                {fu?.due_date && fu.status !== "completed" && (
                  <>
                    <GhostButton onClick={() => followUp("complete")} data-testid="followup-complete-button" className="px-3 py-2">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Done
                    </GhostButton>
                    <GhostButton onClick={() => followUp("snooze", { days: 3 })} data-testid="followup-snooze-button" className="px-3 py-2">
                      <AlarmClockOff className="h-3.5 w-3.5" /> +3d
                    </GhostButton>
                  </>
                )}
              </div>
            </div>
          </section>

          <section className="border border-[#27272A] bg-[#0A0A0A] p-6">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Data & Provenance</h2>
            <div className="mt-4 space-y-2 text-xs">
              <p className="flex justify-between"><span className="text-zinc-500">Source</span><span className="text-zinc-300">{p.source?.provider}</span></p>
              <p className="flex justify-between"><span className="text-zinc-500">Confidence</span><span className="text-zinc-300">{p.source?.confidence}</span></p>
              <p className="flex justify-between"><span className="text-zinc-500">Freshness</span><span className="text-zinc-300">{p.source?.freshness ? new Date(p.source.freshness).toLocaleDateString() : "—"}</span></p>
              {p.provenance && Object.entries(p.provenance).map(([k, v]) => (
                <p key={k} className="flex justify-between">
                  <span className="capitalize text-zinc-500">{k.replace(/_/g, " ")}</span><Prov v={v} />
                </p>
              ))}
            </div>
          </section>

          <section className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="timeline-section">
            <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
              <MessageSquare className="h-4 w-4 text-[#D4AF37]" /> Activity History
            </h2>
            <form onSubmit={addNote} className="mt-4 flex gap-2">
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="add-note-input" className={`${inputCls} flex-1`} />
              <GoldButton type="submit" data-testid="add-note-button" className="px-4">Add</GoldButton>
            </form>
            <ul className="mt-5 space-y-4">
              {[...(p.interactions || [])].reverse().map((i, idx) => (
                <li key={idx} className="border-l border-white/10 pl-4">
                  <p className="text-sm text-zinc-300">{i.note}</p>
                  <p className="mt-0.5 text-[10px] uppercase tracking-wider text-zinc-600">
                    {i.type.replace(/_/g, " ")} · {new Date(i.at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
